import React from 'react'
import moment from 'moment'
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { MAX_RANGE_DAYS, MAX_RESERVATION_READS } from '../utils/extraitems'

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })

const ERRORS = {
  'range-too-long': 'Aikaväli on liian pitkä. Valitse enintään ' + MAX_RANGE_DAYS + ' päivää.',
  'reversed-range': 'Loppupäivä on ennen alkupäivää.',
  'invalid-range': 'Tarkista päivämäärät.',
}

// The read ceiling names the number found, because that is what tells the user how much to
// narrow the range by. Nothing partial is shown: a truncated list would export to CSV looking
// like a complete report.
const errorMessage = (error, found) => {
  if (error === 'too-many-reservations') {
    return (
      'Aikavälillä on ' +
      found +
      ' varausta joilla on lisätuotteita, ja kerralla luetaan enintään ' +
      MAX_RESERVATION_READS +
      '. Rajaa aikaväliä.'
    )
  }
  return ERRORS[error] || 'Raportin muodostaminen ei onnistunut.'
}

// A row can be listed on the strength of the index marker alone, so say which ones did not
// come from the reservation itself rather than letting them look like ordinary rows.
const MISSING_ITEMS_HINT =
  'Varauksella on lisätuotemerkintä, mutta itse tuotteet puuttuvat varaukselta. Nimet on luettu merkinnästä, eikä summaa voi laskea.'
const LOAD_FAILED_HINT =
  'Varauksen tietoja ei saatu luettua. Rivin tiedot ovat päiväindeksistä, ja tuotenimet lisätuotemerkinnästä.'

export default function ExtraItemsReport({ rows, loading, progress, warnings, error, found, startdate, enddate }) {
  const safe = Array.isArray(rows) ? rows : []

  // ';' + utf8WithBom is the house convention - anything else opens as one column in a
  // Finnish Excel.
  const ExportToolbar = () => (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'lisatuotteet-' + (startdate || '') + '_' + (enddate || ''),
          delimiter: ';',
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  )

  const columns = [
    {
      field: 'date',
      headerName: 'Päivä',
      width: 110,
      valueFormatter: (value) => (value ? moment.utc(value, 'YYYY-MM-DD').format('D.M.YYYY') : ''),
    },
    { field: 'starttime', headerName: 'Alkuaika', width: 100 },
    { field: 'duration', headerName: 'Kesto (min)', type: 'number', width: 110 },
    { field: 'resource', headerName: 'Kenttä', width: 160 },
    { field: 'category', headerName: 'Kategoria', width: 130 },
    { field: 'payer', headerName: 'Maksaja', width: 180 },
    { field: 'identifier', headerName: 'Sähköposti / Y-tunnus', width: 220 },
    // Only organisation bookings made by someone who is not a contact person of the
    // organisation have a syncusers entry (reservations.js:3378-3381), so the header says when
    // to expect a value rather than leaving an empty column looking broken.
    { field: 'booker', headerName: 'Varaaja (jos yritys)', width: 190 },
    {
      field: 'extras',
      headerName: 'Lisätuotteet',
      flex: 2,
      minWidth: 220,
      renderCell: (params) => {
        const row = params.row || {}
        const hint = row.loadfailed ? LOAD_FAILED_HINT : row.missingitems ? MISSING_ITEMS_HINT : null
        if (!hint) return params.value
        return (
          <Tooltip title={hint}>
            <span>
              {params.value}
              <Box component='span' sx={{ color: 'warning.main', ml: 0.5, fontWeight: 600 }}>
                *
              </Box>
            </span>
          </Tooltip>
        )
      },
    },
    { field: 'extrascount', headerName: 'Kpl', type: 'number', width: 80 },
    {
      field: 'extrassum',
      headerName: 'Lisätuotteet € (varaushetkellä)',
      type: 'number',
      width: 200,
      // The price is the snapshot copied onto the reservation when it was booked, so it is what
      // was charged then and does not drift when the product catalogue is edited later.
      valueFormatter: (value) => (value === null || value === undefined ? '' : eurFormatter.format(value / 100)),
    },
  ]

  const counts = warnings || {}
  const notes = []
  if (counts.faileddays) notes.push(counts.faileddays + ' päivän varauksia ei saatu luettua')
  if (counts.loadfailed) notes.push(counts.loadfailed + ' varauksen tietoja ei saatu luettua')
  if (counts.missingitems) notes.push(counts.missingitems + ' varaukselta puuttuvat lisätuotetiedot')

  const phase = progress && progress.total > 0 ? progress : null
  const phaselabel = phase
    ? (phase.phase === 'days' ? 'Haetaan päivien varaukset ' : 'Luetaan varauksia ') + phase.done + '/' + phase.total
    : null

  return (
    <Box sx={{ mt: 2 }}>
      {error ? (
        <Alert severity='error' sx={{ mb: 2 }}>
          {errorMessage(error, found)}
        </Alert>
      ) : null}

      {loading && phase ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {phaselabel}
          </Typography>
          <LinearProgress variant='determinate' value={Math.round((phase.done / phase.total) * 100)} sx={{ mt: 0.5 }} />
        </Box>
      ) : null}

      {!loading && notes.length > 0 ? (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {notes.join(', ')}. Puutteelliset rivit on merkitty tähdellä.
        </Alert>
      ) : null}

      {!loading && !error ? (
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {safe.length} varausta, joilla on lisätuotteita
        </Typography>
      ) : null}

      <DataGrid
        autoHeight
        density='compact'
        rows={safe}
        columns={columns}
        loading={loading}
        showToolbar
        slots={{ toolbar: ExportToolbar }}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[20, 50, 100]}
        pagination
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'Ei lisätuotteita valitulla aikavälillä' }}
        sx={{ mt: 1 }}
      />
    </Box>
  )
}
